import React, { useEffect, useState } from 'react';

// Crossfading photo background. The parent needs `relative isolate overflow-hidden`.
// The incoming photo fades in on top of the outgoing one, so the background never dips dark.
export const HeroBackgroundCarousel = ({ images, interval = 6000 }) => {
  const [position, setPosition] = useState({ active: 0, previous: null });
  const [showAll, setShowAll] = useState(false);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const query = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
    setReducedMotion(Boolean(query && query.matches));
    // Only the first photo loads up front so the later ones don't compete with it.
    const timer = setTimeout(() => setShowAll(true), 1500);
    return () => clearTimeout(timer);
  }, []);

  const goTo = (next) =>
    setPosition((current) =>
      current.active === next ? current : { active: next, previous: current.active }
    );

  useEffect(() => {
    if (reducedMotion || !showAll || images.length < 2) return undefined;
    const timer = setInterval(() => {
      if (document.hidden) return;
      setPosition((current) => ({
        active: (current.active + 1) % images.length,
        previous: current.active,
      }));
    }, interval);
    return () => clearInterval(timer);
  }, [reducedMotion, showAll, images.length, interval, position.active]);

  const visible = showAll ? images : images.slice(0, 1);

  const layerClass = (index) => {
    if (index === position.active) return 'opacity-100 z-20';
    if (index === position.previous) return 'opacity-100 z-10';
    return 'opacity-0 z-0';
  };

  return (
    <>
      <div className="absolute inset-0 -z-10" aria-hidden="true">
        {visible.map((src, index) => (
          <img
            key={src}
            src={src}
            alt=""
            decoding="async"
            fetchpriority={index === 0 ? 'high' : undefined}
            className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-1000 ${layerClass(index)}`}
          />
        ))}
      </div>
      {images.length > 1 && (
        <div className="absolute bottom-4 left-0 right-0 z-10 flex justify-center gap-2" role="group" aria-label="Hero photo selector">
          {images.map((src, index) => (
            <button
              key={src}
              type="button"
              aria-label={`Show photo ${index + 1} of ${images.length}`}
              aria-current={index === position.active}
              onClick={() => goTo(index)}
              className={`h-2.5 w-2.5 rounded-full border border-white/80 transition-colors ${
                index === position.active ? 'bg-white' : 'bg-white/20 hover:bg-white/60'
              }`}
            />
          ))}
        </div>
      )}
    </>
  );
};
